import { useQuery } from '@tanstack/react-query';
import { getManufacturerStatus, type ManufacturerStatus } from '../services/manufacturerStatus';
import { MANUFACTURER_STATUS_KEY } from './queryKeys';

/**
 * Whether the guided manufacturer wizard can run for this patient.
 *
 * Pass `enabled: false` to skip the probe when the surrounding UI already knows
 * it won't need it. The backend answers 200 for every "not set up" case, so the
 * caller only has to read the flags.
 */
export function useManufacturerStatus(
    patientId: string | undefined,
    options?: { enabled?: boolean }
) {
    // Narrow once here rather than asserting inside queryFn, which would outlive
    // any later change to `enabled`.
    const id = patientId ?? '';
    const enabled = id !== '' && (options?.enabled ?? true);

    const { data, error, isLoading } = useQuery<ManufacturerStatus>({
        queryKey: MANUFACTURER_STATUS_KEY(id),
        queryFn: () => getManufacturerStatus(id),
        enabled,
        staleTime: 5 * 60 * 1000
    });

    return {
        status: data ?? null,
        manufacturer: data?.manufacturer ?? null,
        automationReady: data?.automationReady ?? false,
        accountConfigured: data?.accountConfigured ?? false,
        isLoading: enabled && isLoading,
        error
    };
}
